import React, { useCallback, useEffect, useState } from 'react';
import { Popup } from '@progress/kendo-react-popup';
import ClickOutside from 'app/widgets/ClickOutside';
import { BaseButton } from 'app/components/buttons/BaseButton';
import { SelectOption } from './types';
import './AlternateSelect.less';
import clsx from 'clsx';
import { CheckIcon } from 'app/components/icons/CheckIcon';

interface Props<T> {
  label: React.ReactNode | string;
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
}

/**
 * It is named 'AlternateSelect' because it serves as a new implementation that has not been used in the codebase before.
 * If this component becomes a standard choice for selection, it can be refactored or renamed to better reflect its role in the application.
 */
export default function AlternateSelect<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: Props<T>) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | undefined>(undefined);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(undefined);
  }, []);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const element = event.currentTarget;
      setAnchorEl(element);
    },
    [],
  );

  useEffect(() => {
    const handleScrollClose = () => {
      if (menuOpen) {
        handleMenuClose();
      }
    };

    const layoutElement = document.querySelector('.layout');
    const layoutContainerElement = document.querySelector('.layout_container');

    layoutElement?.addEventListener('scroll', handleScrollClose);
    layoutContainerElement?.addEventListener('scroll', handleScrollClose);

    return () => {
      layoutElement?.removeEventListener('scroll', handleScrollClose);
      layoutContainerElement?.removeEventListener('scroll', handleScrollClose);
    };
  }, [menuOpen, handleMenuClose]);

  return (
    <>
      <BaseButton className="alternate-select__button" onClick={handleMenuOpen}>
        {label}
      </BaseButton>

      <Popup
        // Note: React doesn't render this popup correctly when we close it onScroll. The key prop says that it needs to be unmounted.
        key={menuOpen ? 'open' : 'closed'}
        anchor={anchorEl}
        appendTo={anchorEl}
        show={menuOpen}
        popupAlign={{ horizontal: 'right', vertical: 'top' }}
      >
        <ClickOutside onClickOutside={handleMenuClose}>
          <ul className="alternate-select__list">
            {options.map(({ label, value: optionValue }, index) => {
              const isSelected = optionValue === value;

              return (
                <li
                  key={`${label}-${index}`}
                  className={clsx('alternate-select__item', {
                    'alternate-select__item--selected': isSelected,
                  })}
                  onClick={event => {
                    onChange(optionValue);
                    handleMenuClose();
                  }}
                >
                  {isSelected && (
                    <CheckIcon className="alternate-select__check-icon" />
                  )}
                  {label}
                </li>
              );
            })}
          </ul>
        </ClickOutside>
      </Popup>
    </>
  );
}
